import React from 'react';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import * as Bixby from '$/core';
import { DeclarationType } from '$/core/enums';
import './index.scss';

interface IDocumentGridProps {
  currentCompany: Bixby.Models.AdminCompany;
  documents: Bixby.Models.CompanyDocument[];
  isDeleteGrid?: boolean;
  isErrorGrid?: boolean;
  isPublishedGrid?: boolean;
  isPendingApprovalGrid?: boolean;
  isDocHostingAdmin: boolean;
  downloadedDocs: Set<string> | undefined;
  formatCompanyDocumentCompanyDocumentCategory: (
    categoryName: string,
    financialDocumentType?: string,
    financialDocumentDate?: Date,
    orgChartPageNumber?: number
  ) => string;
  handleDownload: (id: string) => void;
  handleEditModalOpen: (model: Bixby.Models.CompanyDocument) => void;
  handleRestoreModalOpen: (model: Bixby.Models.CompanyDocument) => void;
  handleDeleteModalOpen: (model: Bixby.Models.CompanyDocument) => void;
  handleSelectedModalOpen: (ids: any) => void;
  onGridReady: (params: any) => void;
  gridRef: any;
}

const DocumentsGrid: React.FC<IDocumentGridProps> = props => {
  const {
    currentCompany,
    documents,
    isDeleteGrid,
    isErrorGrid,
    isPendingApprovalGrid,
    isDocHostingAdmin,
    downloadedDocs,
    formatCompanyDocumentCompanyDocumentCategory,
    handleDownload,
    handleEditModalOpen,
    handleRestoreModalOpen,
    handleDeleteModalOpen,
    handleSelectedModalOpen,
    onGridReady,
    gridRef,
  } = props;

  const getContextMenuItems = () => {
    return ['copy', 'copyWithHeaders', 'separator', 'export'];
  };

  return (
    <>
      <div className="grid_container">
        <div
          className="grid ag-theme-alpine"
          style={{
            minHeight: `calc(${documents.length < 20 ? 50 : documents.length < 30 ? 75 : 100}vh-140px)`,
          }}>
          <AgGridReact
            masterDetail={true}
            getContextMenuItems={getContextMenuItems}
            detailRowAutoHeight={true}
            ref={gridRef}
            rowData={documents}
            defaultColDef={{
              flex: 1,
              minWidth: 100,
              sortable: true,
              resizable: true,
              lockPinned: true,
            }}
            onGridReady={onGridReady}
            rowHeight={32}
            headerHeight={32}
            rowSelection="multiple"
            isRowSelectable={(params: any) => {
              const { documentDeclarationType } = params.data as Bixby.Models.CompanyDocument;
              return !(
                currentCompany?.declarationType === Bixby.Enums.DeclarationType.Private &&
                documentDeclarationType == Bixby.Enums.DeclarationType.Public
              );
            }}
            getRowId={(params: any) => params.data.id}
            columnDefs={[
              { field: 'id', headerName: 'id', hide: true, valueGetter: 'node.id', suppressColumnsToolPanel: true },
              {
                headerName: '',
                checkboxSelection: true,
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
              },
              {
                field: 'publishedDate',
                filter: 'agDateColumnFilter',
                headerName: 'Published Date',
                cellRenderer: (params: any) => {
                  return dayjs.utc(params.value).format('MM/DD/YYYY');
                },
              },
              {
                field: 'createdDate',
                filter: 'agDateColumnFilter',
                headerName: 'Uploaded Date',
                hide: !isDeleteGrid,
                suppressColumnsToolPanel: true,
                cellRenderer: (params: any) => {
                  return dayjs.utc(params.value).format('MM/DD/YYYY');
                },
              },
              {
                field: 'fileName',
                filter: 'agTextColumnFilter',
                headerName: 'Title',
                minWidth: 250,
                floatingFilter: true,
                cellRenderer: (event: any) => {
                  const extension = event.data.fileName.split('.').pop();
                  return extension && extension.length > 0 ? `${event.data.title}.${extension}` : event.data.title;
                },
              },
              { field: 'description', headerName: 'Description' },
              { field: 'documentDeclarationType', headerName: 'Document Declaration' },
              { field: 'client_Name', filter: 'agSetColumnFilter', headerName: 'Client Name' },
              {
                field: 'companyDocumentCompanyDocumentCategories',
                filter: 'agSetColumnFilter',
                headerName: 'Type',
                valueGetter: params => {
                  return params.data?.companyDocumentCompanyDocumentCategories.map(cdcdc =>
                    formatCompanyDocumentCompanyDocumentCategory(
                      cdcdc.categoryName,
                      cdcdc.financialDocumentType,
                      cdcdc.financialDocumentDate,
                      cdcdc.orgChartPageNumber
                    )
                  );
                },
                valueFormatter: params => {
                  return params.value.join('; ');
                },
              },
              {
                field: 'deletedDate',
                filter: 'agDateColumnFilter',
                headerName: 'Deleted Date',
                hide: !isDeleteGrid,
                suppressColumnsToolPanel: true,
                cellRenderer: (params: any) => {
                  return dayjs.utc(params.value).format('MM/DD/YYYY');
                },
              },
              {
                maxWidth: 100,
                headerName: '',
                suppressMenu: true,
                hide: isErrorGrid || (isDeleteGrid && currentCompany.declarationType === DeclarationType.Private),
                suppressColumnsToolPanel: true,
                cellRenderer: (props: any) => {
                  return (
                    <div
                      className="fake_link"
                      role="button"
                      onClick={() => {
                        const { id } = props.data as Bixby.Models.CompanyDocument;
                        handleDownload(id);
                      }}>
                      Download
                    </div>
                  );
                },
              },
              {
                hide: !isDocHostingAdmin || !isPendingApprovalGrid,
                maxWidth: 100,
                headerName: '',
                suppressMenu: true,
                suppressColumnsToolPanel: true,
                cellRenderer: (props: any) => {
                  const { id, documentDeclarationType } = props.data as Bixby.Models.CompanyDocument;
                  const restrictApprove =
                    currentCompany?.declarationType === Bixby.Enums.DeclarationType.Private &&
                    documentDeclarationType == Bixby.Enums.DeclarationType.Public;
                  if (restrictApprove) {
                    return;
                  } else if (downloadedDocs?.has(id)) {
                    return (
                      <div
                        className="fake_link"
                        role="button"
                        onClick={() => {
                          handleSelectedModalOpen([props.data as Bixby.Models.CompanyDocument]);
                        }}>
                        Approve
                      </div>
                    );
                  } else {
                    return <div>Approve</div>;
                  }
                },
              },
              //hard delete
              {
                maxWidth: 100,
                headerName: '',
                filter: 'agTextColumnFilter',
                suppressMenu: true,
                suppressColumnsToolPanel: true,
                hide: !isDocHostingAdmin && isDeleteGrid,
                cellRenderer: (props: any) => {
                  const { documentDeclarationType } = props.data as Bixby.Models.CompanyDocument;
                  const restrictApprove =
                    currentCompany?.declarationType === Bixby.Enums.DeclarationType.Private &&
                    documentDeclarationType == Bixby.Enums.DeclarationType.Public;
                  if (restrictApprove) {
                    return;
                  }
                  return (
                    <div className="fake_link" role="button" onClick={() => handleDeleteModalOpen(props.data)}>
                      {isDeleteGrid ? 'Clear' : 'Delete'}
                    </div>
                  );
                },
              },
              {
                maxWidth: 75,
                headerName: '',
                hide: isDeleteGrid || isErrorGrid,
                suppressColumnsToolPanel: true,
                cellRenderer: (props: any) => {
                  const { documentDeclarationType } = props.data as Bixby.Models.CompanyDocument;
                  const restrict =
                    currentCompany?.declarationType === Bixby.Enums.DeclarationType.Private &&
                    documentDeclarationType == Bixby.Enums.DeclarationType.Public;
                  if (restrict) {
                    return;
                  }
                  return (
                    <div className="fake_link" role="button" onClick={() => handleEditModalOpen(props.data)}>
                      Edit
                    </div>
                  );
                },
              },
              {
                maxWidth: 100,
                headerName: '',
                filter: 'agTextColumnFilter',
                suppressMenu: true,
                suppressColumnsToolPanel: true,
                hide: !isDocHostingAdmin || !isDeleteGrid,
                cellRenderer: (props: any) => {
                  const { documentDeclarationType } = props.data as Bixby.Models.CompanyDocument;
                  const restrict =
                    currentCompany?.declarationType === Bixby.Enums.DeclarationType.Private &&
                    documentDeclarationType == Bixby.Enums.DeclarationType.Public;
                  if (restrict) {
                    return;
                  }
                  return (
                    <div className="fake_link" role="button" onClick={() => handleRestoreModalOpen(props.data)}>
                      Restore
                    </div>
                  );
                },
              },
            ]}></AgGridReact>
        </div>
      </div>
      <div className="grid-total">Total: {documents.length}</div>
    </>
  );
};

export default DocumentsGrid;
